import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetchJson } from '../../apiClient';
import { formatMinutes, bucketFor, EFFORT_META } from './choreEffort';
import './RoutineSuggestions.css';

const unwrap = (data) => {
  const arr = Array.isArray(data) ? data : (data?.$values || []);
  return arr.filter(item => item && !item.$ref);
};

// Matches ChoreRoutineGroup on the server. The API serialises enums as names, but
// accept the numeric form too so this does not break if that ever changes.
const GROUPS = [
  { id: 0, name: 'Neglected', key: 'neglected', icon: '🔧' },
  { id: 1, name: 'Weekly', key: 'weekly', icon: '🔁' },
  { id: 2, name: 'Seasonal', key: 'seasonal', icon: '🍂' }
];

const inGroup = (item, group) =>
  item.group === group.name || item.group === group.id;

function RoutineSuggestions({ instance, rooms = [], onAdopted, showMessage }) {
  const { t, i18n } = useTranslation();
  const [suggestions, setSuggestions] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiFetchJson(
        instance,
        `/api/chore-templates/suggestions?language=${encodeURIComponent(i18n.language || 'en')}`
      );
      const items = unwrap(data);
      setSuggestions(items);
      // Pre-tick the recommended starter set, but never anything already set up.
      setSelected(new Set(items.filter(s => s.recommended && !s.alreadyAdded).map(s => s.key)));
    } catch (e) {
      console.error('Failed to load routine suggestions:', e);
    } finally {
      setLoading(false);
    }
  }, [instance, i18n.language]);

  useEffect(() => { load(); }, [load]);

  const available = useMemo(() => suggestions.filter(s => !s.alreadyAdded), [suggestions]);
  const selectedCount = selected.size;

  const toggle = useCallback((key) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const selectGroup = useCallback((group, on) => {
    setSelected(prev => {
      const next = new Set(prev);
      suggestions
        .filter(s => inGroup(s, group) && !s.alreadyAdded)
        .forEach(s => { if (on) next.add(s.key); else next.delete(s.key); });
      return next;
    });
  }, [suggestions]);

  const handleAdopt = useCallback(async () => {
    if (selectedCount === 0) return;
    try {
      setSaving(true);
      const result = await apiFetchJson(instance, '/api/chore-templates/suggestions', {
        method: 'POST',
        body: JSON.stringify({ keys: Array.from(selected), language: i18n.language || 'en' })
      });
      const count = result?.createdCount ?? 0;
      showMessage?.(t('chores.suggestions.added', { count }));
      setSelected(new Set());
      await load();
      onAdopted?.();
    } catch (e) {
      console.error('Failed to adopt routine suggestions:', e);
    } finally {
      setSaving(false);
    }
  }, [instance, selected, selectedCount, i18n.language, showMessage, t, load, onAdopted]);

  if (loading && suggestions.length === 0) return null;

  if (available.length === 0 && suggestions.length > 0) {
    return (
      <div className="routine-suggestions routine-suggestions-done">
        ✅ {t('chores.suggestions.allAdded')}
      </div>
    );
  }

  return (
    <div className="routine-suggestions">
      <button
        type="button"
        className="routine-suggestions-toggle"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
      >
        <span className="routine-suggestions-toggle-main">
          ✨ {t('chores.suggestions.title')}
        </span>
        <span className="routine-suggestions-count">
          {t('chores.suggestions.availableCount', { count: available.length })}
        </span>
        <span aria-hidden="true">{open ? '▾' : '▸'}</span>
      </button>

      {open && (
        <div className="routine-suggestions-body">
          <p className="routine-suggestions-intro">{t('chores.suggestions.intro')}</p>

          {GROUPS.map(group => {
            const items = suggestions.filter(s => inGroup(s, group));
            if (items.length === 0) return null;
            const selectable = items.filter(s => !s.alreadyAdded);
            const allOn = selectable.length > 0 && selectable.every(s => selected.has(s.key));

            return (
              <div key={group.key} className="routine-group">
                <div className="routine-group-header">
                  <div>
                    <strong>{group.icon} {t(`chores.suggestions.groups.${group.key}.title`)}</strong>
                    <span className="routine-group-blurb">
                      {t(`chores.suggestions.groups.${group.key}.blurb`)}
                    </span>
                  </div>
                  {selectable.length > 0 && (
                    <button
                      type="button"
                      className="chore-btn chore-btn-secondary chore-btn-small"
                      onClick={() => selectGroup(group, !allOn)}
                    >
                      {allOn ? t('chores.suggestions.clearAll') : t('chores.suggestions.selectAll')}
                    </button>
                  )}
                </div>

                <ul className="routine-list">
                  {items.map(item => {
                    const room = rooms.find(r => r.id === item.roomId);
                    const effort = bucketFor(item.estimatedMinutes);
                    return (
                      <li
                        key={item.key}
                        className={`routine-item ${item.alreadyAdded ? 'routine-item-added' : ''}`}
                      >
                        <label className="routine-item-label">
                          <input
                            type="checkbox"
                            checked={selected.has(item.key)}
                            disabled={item.alreadyAdded || saving}
                            onChange={() => toggle(item.key)}
                          />
                          <span className="routine-item-title">{item.title}</span>
                        </label>
                        <div className="routine-item-meta">
                          <span className="routine-chip">{item.frequencyLabel}</span>
                          <span className={`routine-chip ${EFFORT_META[effort].className}`}>
                            {EFFORT_META[effort].icon} {formatMinutes(item.estimatedMinutes, t)}
                          </span>
                          {room && (
                            <span className="routine-chip">
                              {room.icon || '🏠'}{' '}
                              {(i18n.language === 'no' ? room.nameNo : room.nameEn) || room.nameEn}
                            </span>
                          )}
                          {item.alreadyAdded && (
                            <span className="routine-chip routine-chip-added">
                              ✅ {t('chores.suggestions.alreadyAdded')}
                            </span>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}

          <div className="routine-suggestions-actions">
            <button
              type="button"
              className="chore-btn chore-btn-primary"
              onClick={handleAdopt}
              disabled={selectedCount === 0 || saving}
            >
              {saving
                ? t('chores.suggestions.adding')
                : t('chores.suggestions.addSelected', { count: selectedCount })}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default RoutineSuggestions;
